import { pickBy } from "lodash";

import { RangeOptions } from "components/UseQueryParamsControls/FilterControls/RangeFilterControl";
import {
  UiParam,
  UiParams,
  ValidDynamicRestUrlParams,
} from "hooks/useQueryParams/types";
import {
  ALL_10_YEAR_TERMS,
  ALL_30_YEAR_TERMS,
  ALL_FIXED_RETURN_TERMS,
  ALL_LIFE_OF_RIGHTS_TERMS,
} from "services/listings/constants";
import { LISTING_DEAL_STATES } from "services/listings/constants";

export const validDynamicRestUrlParams: ValidDynamicRestUrlParams = {
  "filter{state.in}": {
    path: "state.in",
    key: "state.in",
    option: "filter",
    internal: true,
  },
  "filter{seller_id}": {
    path: "onlyYourListings",
    key: "seller_id",
    option: "filter",
    requiresLogin: true,
    omitToDisable: true,
  },
  "filter{term.in}": {
    path: "term.in",
    key: "term.in",
    option: "filter",
  },
  "filter{converted_deal_amount.gte}": {
    path: "converted_deal_amount.gte",
    key: "converted_deal_amount.gte",
    option: "filter",
  },
  "filter{converted_deal_amount.lte}": {
    path: "converted_deal_amount.lte",
    key: "converted_deal_amount.lte",
    option: "filter",
  },
  "filter{ltm.gte}": {
    path: "ltm.gte",
    key: "ltm.gte",
    option: "filter",
  },
  "filter{ltm.lte}": {
    path: "ltm.lte",
    key: "ltm.lte",
    option: "filter",
  },
  "filter{dollar_age.gte}": {
    path: "dollar_age.gte",
    key: "dollar_age.gte",
    option: "filter",
  },
  "filter{dollar_age.lte}": {
    path: "dollar_age.lte",
    key: "dollar_age.lte",
    option: "filter",
  },
  "filter{marketplace_median.gte}": {
    path: "marketplace_median.gte",
    key: "marketplace_median.gte",
    option: "filter",
    requiresLogin: true,
  },
  "filter{marketplace_median.lte}": {
    path: "marketplace_median.lte",
    key: "marketplace_median.lte",
    option: "filter",
    requiresLogin: true,
  },
  "sort[]": {
    path: "sort",
    key: "sort",
    option: "sort",
  },
  only_favorited: {
    path: "onlyFavorited",
    key: "only_favorited",
    option: "custom",
    requiresLogin: true,
  },
  page: {
    path: "page",
    key: "page",
    option: "page",
  },
  page_size: {
    path: "pageSize",
    key: "pageSize",
    option: "pageSize",
    internal: true,
  },
  q: {
    path: "search",
    key: "search",
    option: "search",
  },
};

export const validUiSortOptions: UiParam[] = [
  {
    key: "sort",
    value: ["-deal_date", "-published_date"],
    name: "Sale Date",
    direction: "desc",
    option: "sort",
  },
  {
    key: "sort",
    value: ["deal_date", "-published_date"],
    name: "Sale Date",
    direction: "asc",
    option: "sort",
  },
  {
    key: "sort",
    value: ["-converted_deal_amount", "-published_date"],
    name: "Sale Price",
    direction: "desc",
    option: "sort",
  },
  {
    key: "sort",
    value: ["converted_deal_amount", "-published_date"],
    name: "Sale Price",
    direction: "asc",
    option: "sort",
  },
  {
    key: "sort",
    value: ["-ltm", "-published_date"],
    name: "LTM",
    direction: "desc",
    option: "sort",
  },
  {
    key: "sort",
    value: ["ltm", "-published_date"],
    name: "LTM",
    direction: "asc",
    option: "sort",
  },
  {
    key: "sort",
    value: ["-dollar_age", "-published_date"],
    name: "Dollar Age",
    direction: "desc",
    option: "sort",
  },
  {
    key: "sort",
    value: ["dollar_age", "-published_date"],
    name: "Dollar Age",
    direction: "asc",
    option: "sort",
  },
  {
    key: "sort",
    value: ["-marketplace_median", "-published_date"],
    name: "Marketplace Median",
    direction: "desc",
    option: "sort",
    requiresLogin: true,
  },
  {
    key: "sort",
    value: ["marketplace_median", "-published_date"],
    name: "Marketplace Median",
    direction: "asc",
    option: "sort",
    requiresLogin: true,
  },
];
export const defaultSortOption = validUiSortOptions[0];

export const termFilterOptions: UiParam[] = [
  {
    key: "filter",
    value: ALL_LIFE_OF_RIGHTS_TERMS,
    name: "Life of Rights",
    option: "filter",
  },
  {
    key: "filter",
    value: ALL_30_YEAR_TERMS,
    name: "30-Year",
    option: "filter",
  },
  {
    key: "filter",
    value: ALL_10_YEAR_TERMS,
    name: "10-Year",
    option: "filter",
  },
  {
    key: "filter",
    value: ALL_FIXED_RETURN_TERMS,
    name: "Fixed Return",
    option: "filter",
  },
];

export const rangeOptionsLtm: RangeOptions = {
  "$0 - $5k": {
    gte: { key: "filter", value: [], option: "filter" },
    lte: { key: "filter", value: ["5000"], option: "filter" },
  },
  "$5k - $10k": {
    gte: { key: "filter", value: ["5000"], option: "filter" },
    lte: { key: "filter", value: ["10000"], option: "filter" },
  },
  "$10K - $50K": {
    gte: { key: "filter", value: ["10000"], option: "filter" },
    lte: { key: "filter", value: ["50000"], option: "filter" },
  },
  "$50K - $250K": {
    gte: { key: "filter", value: ["50000"], option: "filter" },
    lte: { key: "filter", value: ["250000"], option: "filter" },
  },
  "$250K+": {
    gte: { key: "filter", value: ["250000"], option: "filter" },
    lte: { key: "filter", value: [], option: "filter" },
  },
};
export const rangeOptionsDollarAge: RangeOptions = {
  "0 - 3": {
    gte: { key: "filter", value: [], option: "filter" },
    lte: { key: "filter", value: ["3"], option: "filter" },
  },
  "3 - 5": {
    gte: { key: "filter", value: ["3"], option: "filter" },
    lte: { key: "filter", value: ["5"], option: "filter" },
  },
  "5 - 7": {
    gte: { key: "filter", value: ["5"], option: "filter" },
    lte: { key: "filter", value: ["7"], option: "filter" },
  },
  "7 - 10": {
    gte: { key: "filter", value: ["7"], option: "filter" },
    lte: { key: "filter", value: ["10"], option: "filter" },
  },
  "10+": {
    gte: { key: "filter", value: ["10"], option: "filter" },
    lte: { key: "filter", value: [], option: "filter" },
  },
};
export const rangeOptionsMarketplaceMedian: RangeOptions = {
  "$0 - $10k": {
    gte: { key: "filter", value: [], option: "filter" },
    lte: { key: "filter", value: ["10000"], option: "filter" },
  },
  "$10K - $50K": {
    gte: { key: "filter", value: ["10000"], option: "filter" },
    lte: { key: "filter", value: ["50000"], option: "filter" },
  },
  "$50K - $250K": {
    gte: { key: "filter", value: ["50000"], option: "filter" },
    lte: { key: "filter", value: ["250000"], option: "filter" },
  },
  "$250K - $1M": {
    gte: { key: "filter", value: ["250000"], option: "filter" },
    lte: { key: "filter", value: ["1000000"], option: "filter" },
  },
  "$1M+": {
    gte: { key: "filter", value: ["1000000"], option: "filter" },
    lte: { key: "filter", value: [], option: "filter" },
  },
};

export const rangeOptionsSalePrice: RangeOptions = {
  "$0 - $10k": {
    gte: { key: "filter", value: [], option: "filter" },
    lte: { key: "filter", value: ["10000"], option: "filter" },
  },
  "$10k - $50k": {
    gte: { key: "filter", value: ["10000"], option: "filter" },
    lte: { key: "filter", value: ["50000"], option: "filter" },
  },
  "$50K - $250K": {
    gte: { key: "filter", value: ["50000"], option: "filter" },
    lte: { key: "filter", value: ["250000"], option: "filter" },
  },
  "$250K - $1M": {
    gte: { key: "filter", value: ["250000"], option: "filter" },
    lte: { key: "filter", value: ["1000000"], option: "filter" },
  },
  "$1M+": {
    gte: { key: "filter", value: ["1000000"], option: "filter" },
    lte: { key: "filter", value: [], option: "filter" },
  },
};

export const makeDefaultUiParams = (
  userId: number,
  isListView: boolean,
): UiParams => {
  const isAuthenticated = !!userId;
  const baseParams: UiParams = {
    "state.in": {
      key: "state.in",
      value: [...LISTING_DEAL_STATES, "closed"],
      option: "filter",
      internal: true,
    },
    "term.in": {
      key: "term.in",
      value: [],
      option: "filter",
    },
    "ltm.gte": {
      key: "ltm.gte",
      value: [],
      option: "filter",
    },
    "ltm.lte": {
      key: "ltm.lte",
      value: [],
      option: "filter",
    },
    "dollar_age.gte": {
      key: "dollar_age.gte",
      value: [],
      option: "filter",
    },
    "dollar_age.lte": {
      key: "dollar_age.lte",
      value: [],
      option: "filter",
    },
    "marketplace_median.gte": {
      key: "marketplace_median.gte",
      value: [],
      option: "filter",
    },
    "marketplace_median.lte": {
      key: "marketplace_median.lte",
      value: [],
      option: "filter",
    },
    "converted_deal_amount.gte": {
      key: "converted_deal_amount.gte",
      value: [],
      option: "filter",
    },
    "converted_deal_amount.lte": {
      key: "converted_deal_amount.lte",
      value: [],
      option: "filter",
    },
    onlyFavorited: {
      key: "only_favorited",
      value: "0",
      option: "custom",
      requiresLogin: true,
    },
    sort: defaultSortOption,
    page: {
      key: "page",
      value: "1",
      option: "page",
    },
    pageSize: {
      key: "pageSize",
      value: isListView ? "50" : "15",
      option: "pageSize",
      internal: true,
    },
    search: { key: "search", value: "", option: "search" },
  };
  return pickBy(
    baseParams,
    param => !param.requiresLogin || (param.requiresLogin && isAuthenticated),
  );
};
